import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Calculator, Hash } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  lettersTransformed: number;
  uniqueKeyPairs: number;
  averageKeyA: number;
  averageKeyB: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  keyA: number;
  keyB: number;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  lettersCount: number;
}

const AffineCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [keyA, setKeyA] = useState<number>(5);
  const [keyB, setKeyB] = useState<number>(8);
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'keys' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [includeNumbers, setIncludeNumbers] = useState<boolean>(false);
  const [removeSpaces, setRemoveSpaces] = useState<boolean>(false);
  const [showFunction, setShowFunction] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(false);
  const [groupSize, setGroupSize] = useState<number>(5);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    lettersTransformed: 0,
    uniqueKeyPairs: 0,
    averageKeyA: 0,
    averageKeyB: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [usedKeyPairs, setUsedKeyPairs] = useState<Set<string>>(new Set());

  // Mathematical helper functions
  const gcd = (a: number, b: number): number => b === 0 ? Math.abs(a) : gcd(b, a % b);

  const modInverse = (a: number, m: number): number => {
    if (gcd(a, m) !== 1) return -1;
    
    let m0 = m;
    let x0 = 0;
    let x1 = 1;
    
    if (m === 1) return 0;
    
    while (a > 1) {
      const q = Math.floor(a / m);
      let t = m;
      m = a % m;
      a = t;
      t = x0;
      x0 = x1 - q * x0;
      x1 = t;
    }
    
    if (x1 < 0) x1 += m0;
    return x1;
  };

  const isValidKeyA = (a: number): boolean => {
    return gcd(a, 26) === 1;
  };

  const getValidKeysA = (): number[] => {
    return [1, 3, 5, 7, 9, 11, 15, 17, 19, 21, 23, 25];
  };

  // Affine cipher implementation
  const affineCipher = useCallback((text: string, a: number, b: number, isEncrypt: boolean): string => {
    if (!text) return '';
    if (!isValidKeyA(a)) return 'ERROR: Key A must be coprime to 26';
    
    const processedText = removeSpaces ? text.replace(/\s/g, '') : text;
    
    return processedText.split('').map(char => {
      if (/[a-zA-Z]/.test(char)) {
        const isUpperCase = char === char.toUpperCase();
        const charCode = char.toUpperCase().charCodeAt(0) - 65;
        
        let resultCode;
        if (isEncrypt) {
          // Encryption: E(x) = (ax + b) mod 26
          resultCode = (a * charCode + b) % 26;
        } else {
          // Decryption: D(x) = a^(-1)(x - b) mod 26
          const aInverse = modInverse(a, 26);
          if (aInverse === -1) return char; // Should not happen if keyA is valid
          resultCode = (aInverse * (charCode - b + 26)) % 26;
        }
        
        const newChar = String.fromCharCode(resultCode + 65);
        
        if (preserveCase) {
          return isUpperCase ? newChar : newChar.toLowerCase();
        }
        return newChar;
      } else if (includeNumbers && /[0-9]/.test(char)) {
        const digit = parseInt(char);
        let resultDigit;
        
        if (isEncrypt) {
          resultDigit = (a * digit + b) % 10;
        } else {
          const aInverse = modInverse(a, 10);
          if (aInverse === -1) return char;
          resultDigit = (aInverse * (digit - b + 10)) % 10;
        }
        
        return resultDigit.toString();
      }
      
      return char;
    }).join('');
  }, [preserveCase, includeNumbers, removeSpaces]);

  // Process text - separated cipher logic from analytics
  const processText = useCallback(() => {
    if (!inputText) {
      setOutputText('');
      return '';
    }
    
    const result = affineCipher(inputText, keyA, keyB, operation === 'encrypt');
    
    let finalResult = result;
    if (groupOutput && result && !result.startsWith('ERROR')) {
      const cleanResult = result.replace(/\s/g, '');
      const groups = [];
      for (let i = 0; i < cleanResult.length; i += groupSize) {
        groups.push(cleanResult.slice(i, i + groupSize));
      }
      finalResult = groups.join(' ');
    }
    
    setOutputText(finalResult);
    return finalResult;
  }, [inputText, keyA, keyB, operation, affineCipher, groupOutput, groupSize]);

  // Update analytics separately to avoid infinite loops
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !result || result.startsWith('ERROR')) return;
    
    const lettersCount = inputText.replace(/[^a-zA-Z]/g, '').length;
    const keyPairString = `${keyA}-${keyB}`;
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      keyA: keyA,
      keyB: keyB,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      lettersCount: lettersCount
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setUsedKeyPairs(prev => {
      const newSet = new Set(prev).add(keyPairString);
      
      setStats(prevStats => {
        const newTotalOps = prevStats.totalOperations + 1;
        const newTotalChars = prevStats.totalCharactersProcessed + inputText.length;
        const newLettersTransformed = prevStats.lettersTransformed + lettersCount;
        
        // Calculate average keys
        const allKeyPairs = Array.from(newSet).map(pair => pair.split('-').map(Number));
        const avgA = Math.round(allKeyPairs.reduce((sum, [a]) => sum + a, 0) / allKeyPairs.length);
        const avgB = Math.round(allKeyPairs.reduce((sum, [, b]) => sum + b, 0) / allKeyPairs.length);
        
        return {
          ...prevStats,
          totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
          totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
          totalOperations: newTotalOps,
          totalCharactersProcessed: newTotalChars,
          averageTextLength: Math.round(newTotalChars / newTotalOps),
          longestText: Math.max(prevStats.longestText, inputText.length),
          lettersTransformed: newLettersTransformed,
          uniqueKeyPairs: newSet.size,
          averageKeyA: avgA,
          averageKeyB: avgB
        };
      });
      
      return newSet;
    });
  }, [inputText, keyA, keyB, operation]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText) {
      const result = processText();
      if (result && !result.startsWith('ERROR')) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, keyA, keyB, operation]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setKeyA(5);
    setKeyB(8);
    setOperation('encrypt');
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "AFFINE CIPHER USES LINEAR MATHEMATICS",
      "MODULAR ARITHMETIC SECURES MESSAGES",
      "COPRIME NUMBERS ENABLE DECRYPTION",
      "LINEAR FUNCTION TRANSFORMS LETTERS",
      "MATHEMATICAL CRYPTOGRAPHY PRINCIPLES",
      "MULTIPLICATIVE AND ADDITIVE KEYS"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKeys = () => {
    const validAs = getValidKeysA();
    const newA = validAs[Math.floor(Math.random() * validAs.length)];
    const newB = Math.floor(Math.random() * 26);
    setKeyA(newA);
    setKeyB(newB);
  };

  const setKeyAValue = (value: number) => {
    if (isValidKeyA(value)) {
      setKeyA(value);
    }
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Affine Cipher - Mathematical Linear Function Encryption Tool | Modular Arithmetic Cryptography</title>
        <meta name="description" content="Professional Affine Cipher tool using linear mathematical functions with multiplicative and additive keys. Features modular arithmetic, coprime validation, and educational cryptography demonstrations for learning classical cipher systems." />
        <meta name="keywords" content="affine cipher, linear cipher, modular arithmetic, coprime numbers, mathematical encryption, multiplicative cipher, additive cipher, classical cryptography, monoalphabetic substitution, linear function cipher" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Affine Cipher - Mathematical Linear Function Encryption" />
        <meta property="og:description" content="Advanced Affine Cipher with linear mathematical functions, modular arithmetic validation, and educational features for classical cryptography learning." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Affine Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Affine Cipher - Linear Mathematical Encryption" />
        <meta name="twitter:description" content="Professional Affine Cipher with modular arithmetic and coprime validation. Educational cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@affinecipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Affine Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Mathematical/Educational Keywords */}
        <meta name="subject" content="Mathematics, Modular Arithmetic, Cryptography, Number Theory" />
        <meta name="classification" content="Education, Mathematics, Computer Science, Cryptography" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Affine Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Affine Cipher Tool",
            "alternateName": ["Affine Cipher Calculator", "Linear Function Cipher", "Modular Arithmetic Cipher"],
            "description": "Advanced Affine Cipher tool for mathematical linear function encryption using multiplicative and additive keys with modular arithmetic validation and educational features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["EducationalApplication", "MathApplication", "SecurityApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Linear function encryption and decryption",
              "Modular arithmetic operations",
              "Coprime number validation",
              "Multiplicative and additive key support",
              "Mathematical function visualization",
              "Educational cryptography demonstrations",
              "Real-time key validation",
              "Classical cipher analysis",
              "Number theory applications",
              "Analytics and usage statistics",
              "Dark and light themes",
              "File upload and download"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Affine Cipher Tool",
              "description": "Mathematical cryptography and number theory education tools"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Mathematics and Computer Science Education",
              "targetName": "Modular Arithmetic and Linear Functions in Cryptography"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "practice", "demonstration"],
            "typicalAgeRange": "14-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "about": [
              {
                "@type": "Thing",
                "name": "Modular Arithmetic"
              },
              {
                "@type": "Thing",
                "name": "Linear Functions"
              },
              {
                "@type": "Thing",
                "name": "Number Theory"
              },
              {
                "@type": "Thing",
                "name": "Classical Cryptography"
              }
            ],
            "teaches": [
              "Linear function applications in cryptography",
              "Modular arithmetic operations",
              "Coprime number identification",
              "Classical substitution ciphers",
              "Mathematical cipher analysis"
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.6",
              "ratingCount": "1123",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
        
        {/* Mathematical Education Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "Affine Cipher Mathematical Tutorial",
            "description": "Interactive learning tool for understanding linear function cryptography through the Affine cipher, demonstrating modular arithmetic and number theory applications.",
            "educationalLevel": "intermediate",
            "teaches": [
              "Linear function cryptography",
              "Modular arithmetic with encryption",
              "Coprime number applications",
              "Mathematical cipher design",
              "Classical cryptanalysis methods"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "practice", "research"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher", "researcher"],
              "audienceType": ["mathematics student", "computer science student", "cryptography enthusiast"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Applied Mathematics"
              },
              {
                "@type": "Thing",
                "name": "Number Theory"
              },
              {
                "@type": "Thing",
                "name": "Cryptographic Functions"
              }
            ],
            "educationalCredentialAwarded": "Understanding of mathematical cryptography and modular arithmetic"
          })}
        </script>
        
        {/* Number Theory Tool Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Affine Cipher Mathematical Calculator",
            "description": "Professional mathematical tool for Affine cipher operations including modular arithmetic, coprime validation, and linear function processing.",
            "applicationCategory": "MathApplication",
            "operatingSystem": "Cross-platform",
            "softwareRequirements": "Modern web browser with JavaScript support",
            "featureList": [
              "Modular arithmetic calculation",
              "Coprime number validation",
              "Linear function encryption processing",
              "Multiplicative inverse calculation",
              "GCD computation for key validation"
            ],
            "targetAudience": {
              "@type": "ProfessionalAudience",
              "audienceType": ["Mathematician", "Cryptographer", "Computer Scientist", "Mathematics Teacher"]
            },
            "programmingLanguage": "JavaScript",
            "runtimePlatform": "Web Browser"
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Hash className="text-orange-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Affine Cipher</h1>
                <p className="text-sm opacity-60">Linear Mathematical Encryption</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setActiveModal(activeModal === 'keys' ? null : 'keys')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Keys & Function"
              >
                <Calculator size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Key A (Multiplicative)</label>
                    <span className={`text-xs px-2 py-1 rounded ${
                      isValidKeyA(keyA) ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                    }`}>
                      {isValidKeyA(keyA) ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                  <select
                    value={keyA}
                    onChange={(e) => setKeyA(Number(e.target.value))}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    {getValidKeysA().map(key => (
                      <option key={key} value={key}>{key}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Key B (Additive)</label>
                  <input
                    type="number"
                    value={keyB}
                    onChange={(e) => setKeyB(Number(e.target.value))}
                    min="0"
                    max="25"
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateRandomKeys}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-orange-600 border-orange-500 hover:bg-orange-700 text-white' : 'bg-orange-500 border-orange-400 hover:bg-orange-600 text-white'
                    } transition-colors`}
                    title="Generate random valid keys"
                  >
                    <Hash size={16} />
                    <span>Random</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Calculator size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Function Display */}
              {showFunction && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-2">Mathematical Functions:</h4>
                  <div className="font-mono text-sm space-y-1">
                    <div className="text-green-500">
                      <strong>Encryption:</strong> E(x) = ({keyA}x + {keyB}) mod 26
                    </div>
                    <div className="text-blue-500">
                      <strong>Decryption:</strong> D(x) = {modInverse(keyA, 26)}(x - {keyB}) mod 26
                    </div>
                    <div className="text-xs opacity-60 mt-2">
                      Where x is the letter position (A=0, B=1, ..., Z=25)
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Affine cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>

              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'affine-cipher-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Keys & Function Modal */}
        {activeModal === 'keys' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Calculator className="text-orange-500" />
                  <span>Keys & Function</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Key A (Multiplicative)</label>
                  <div className="grid grid-cols-6 gap-2">
                    {getValidKeysA().map(key => (
                      <button
                        key={key}
                        onClick={() => setKeyA(key)}
                        className={`p-2 text-sm rounded border ${
                          keyA === key
                            ? (isDarkMode ? 'bg-orange-600 border-orange-500 text-white' : 'bg-orange-500 border-orange-400 text-white')
                            : (isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200')
                        } transition-colors`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                  <div className="text-xs opacity-60 mt-1">
                    Only coprime numbers to 26 are valid
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Key B (Additive): {keyB}</label>
                  <input
                    type="range"
                    min="0"
                    max="25"
                    value={keyB}
                    onChange={(e) => setKeyB(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs opacity-60 mt-1">
                    <span>0</span>
                    <span>25</span>
                  </div>
                </div>
                
                <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h4 className="text-sm font-medium mb-2">Current Functions:</h4>
                  <div className="font-mono text-xs space-y-1">
                    <div className="text-green-500">E(x) = ({keyA}x + {keyB}) mod 26</div>
                    <div className="text-blue-500">D(x) = {modInverse(keyA, 26)}(x - {keyB}) mod 26</div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={generateRandomKeys}
                    className={`flex-1 p-2 text-sm rounded border ${
                      isDarkMode ? 'bg-orange-600 border-orange-500 hover:bg-orange-700 text-white' : 'bg-orange-500 border-orange-400 hover:bg-orange-600 text-white'
                    } transition-colors`}
                  >
                    Generate Random
                  </button>
                  <button
                    onClick={() => {
                      setKeyA(5);
                      setKeyB(8);
                    }}
                    className={`flex-1 p-2 text-sm rounded border ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                  >
                    Default (5,8)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Hash className="text-orange-500" />
                  <span>About Affine Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Affine Cipher?</h3>
                  <p>The Affine cipher is a monoalphabetic substitution cipher that uses a mathematical linear function to encrypt letters. It combines multiplicative and additive transformations using two keys: 'a' (multiplicative) and 'b' (additive).</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Mathematical Foundation</h3>
                  <ul className="space-y-1">
                    <li><strong>Encryption:</strong> E(x) = (ax + b) mod 26</li>
                    <li><strong>Decryption:</strong> D(x) = a⁻¹(x - b) mod 26</li>
                    <li><strong>Linear Function:</strong> Combines multiplication and addition</li>
                    <li><strong>Modular Arithmetic:</strong> All operations modulo 26</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Key Requirements</h3>
                  <ul className="space-y-1">
                    <li><strong>Key A (Multiplicative):</strong> Must be coprime to 26</li>
                    <li><strong>Valid A values:</strong> 1, 3, 5, 7, 9, 11, 15, 17, 19, 21, 23, 25</li>
                    <li><strong>Key B (Additive):</strong> Can be any integer 0-25</li>
                    <li><strong>Coprimality:</strong> gcd(a, 26) = 1 for decryption to work</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Mathematical Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} font-mono text-xs space-y-2`}>
                    <div><strong>Keys:</strong> a = 5, b = 8</div>
                    <div><strong>Encryption:</strong> E(x) = (5x + 8) mod 26</div>
                    <div><strong>Letter H (x=7):</strong> E(7) = (5×7 + 8) mod 26 = 43 mod 26 = 17 = R</div>
                    <div><strong>Decryption:</strong> D(x) = 21(x - 8) mod 26</div>
                    <div><strong>Letter R (x=17):</strong> D(17) = 21(17-8) mod 26 = 21×9 mod 26 = 7 = H</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Why Coprime Numbers?</h3>
                  <p>Key 'a' must be coprime to 26 because we need to find its multiplicative inverse for decryption. If gcd(a, 26) ≠ 1, then a⁻¹ mod 26 doesn't exist, making decryption impossible.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>More secure than simple Caesar cipher</li>
                    <li>Easy to implement and understand</li>
                    <li>Good introduction to mathematical cryptography</li>
                    <li>Demonstrates modular arithmetic principles</li>
                    <li>Foundation for more complex cipher systems</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Vulnerabilities</h3>
                  <ul className="space-y-1">
                    <li>Still vulnerable to frequency analysis</li>
                    <li>Limited key space (only 312 possible keys)</li>
                    <li>Monoalphabetic substitution weaknesses</li>
                    <li>Pattern analysis can reveal structure</li>
                    <li>Known plaintext attacks are possible</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Educational Applications</h3>
                  <ul className="space-y-1">
                    <li>Teaching modular arithmetic</li>
                    <li>Demonstrating linear functions</li>
                    <li>Introduction to number theory</li>
                    <li>Understanding coprime relationships</li>
                    <li>Classical cryptography foundations</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-yellow-600">⚠️ <strong>Low Security:</strong> The Affine cipher is easily breakable with modern methods and should not be used for actual security. It's excellent for educational purposes and understanding mathematical cryptography principles.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Case</label>
                  <input
                    type="checkbox"
                    checked={preserveCase}
                    onChange={(e) => setPreserveCase(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Include Numbers</label>
                  <input
                    type="checkbox"
                    checked={includeNumbers}
                    onChange={(e) => setIncludeNumbers(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Remove Spaces</label>
                  <input
                    type="checkbox"
                    checked={removeSpaces}
                    onChange={(e) => setRemoveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Mathematical Function</label>
                  <input
                    type="checkbox"
                    checked={showFunction}
                    onChange={(e) => setShowFunction(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="number"
                      value={groupSize}
                      onChange={(e) => setGroupSize(Number(e.target.value))}
                      min="2"
                      max="10"
                      className={`w-full p-2 rounded border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group for formatted output
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Key Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setKeyA(5);
                        setKeyB(8);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-orange-600 border-orange-500 hover:bg-orange-700 text-white' : 'bg-orange-500 border-orange-400 hover:bg-orange-600 text-white'
                      } transition-colors`}
                    >
                      Classic (a=5, b=8)
                    </button>
                    
                    <button
                      onClick={() => {
                        setKeyA(3);
                        setKeyB(7);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Simple (a=3, b=7)
                    </button>
                    
                    <button
                      onClick={() => {
                        setKeyA(21);
                        setKeyB(13);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                      } transition-colors`}
                    >
                      Complex (a=21, b=13)
                    </button>
                    
                    <button
                      onClick={() => {
                        setPreserveCase(true);
                        setIncludeNumbers(false);
                        setRemoveSpaces(false);
                        setShowFunction(true);
                        setGroupOutput(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Educational Mode
                    </button>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Current Configuration</h3>
                  <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-xs space-y-1`}>
                    <div><strong>Key A:</strong> {keyA} {isValidKeyA(keyA) ? '(Valid)' : '(Invalid)'}</div>
                    <div><strong>Key B:</strong> {keyB}</div>
                    <div><strong>Function:</strong> E(x) = ({keyA}x + {keyB}) mod 26</div>
                    <div><strong>Inverse:</strong> a⁻¹ = {modInverse(keyA, 26)} mod 26</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.lettersTransformed}</div>
                  <div className="text-sm">Letters Transformed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.uniqueKeyPairs}</div>
                  <div className="text-sm">Unique Key Pairs</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.averageKeyA}</div>
                  <div className="text-sm">Average Key A</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageKeyB}</div>
                  <div className="text-sm">Average Key B</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-indigo-500">{stats.averageTextLength}</div>
                  <div className="text-sm">Avg Text Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-orange-500 text-white">
                            a={item.keyA}, b={item.keyB}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.lettersCount} letters
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Hash size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Mathematical Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-orange-500">Key A Distribution:</div>
                      <div>{stats.averageKeyA} (avg of {stats.uniqueKeyPairs} pairs)</div>
                    </div>
                    <div>
                      <div className="text-cyan-500">Key B Distribution:</div>
                      <div>{stats.averageKeyB} (avg of {stats.uniqueKeyPairs} pairs)</div>
                    </div>
                    <div>
                      <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                    </div>
                    <div>
                      <div className="text-purple-500">Letter Efficiency:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.lettersTransformed / stats.totalCharactersProcessed) * 100) : 0}%</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AffineCipherApp;